import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { checkUsersFetching, getActiveOrganization, getAllUsersData } from "selectors/organizations";
import { getAllBusinessesData, getSelectedBusinessData } from "selectors/businesses";
import { getCustomPromptsData } from "selectors/customPrompts";
import { getTextsData } from "selectors/texts";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import AiBookkeeperTabAutomation from "./lib/AiBookkeeperTabAutomation";
import AiBookkeeperTabDocuments from "./lib/AiBookkeeperTabDocuments";
import AiBookkeeperTabGeneral from "./lib/AiBookkeeperTabGeneral";
import AiBookkeeperTabProcessing from "./lib/AiBookkeeperTabProcessing";
import AiBookkeeperTabPrompts from "./lib/AiBookkeeperTabPrompts";
import Businesses from "utils/Businesses";
import BusinessesActions from "actions/BusinessesActions";
import Buttons from "./lib/Buttons";
import ClientPortalTab from "./lib/ClientPortalTab";
import CustomPromptTypes from "const/CustomPromptTypes";
import CustomPromptsActions from "actions/CustomPromptsActions";
import Dates from "utils/Dates";
import DocumentsTab from "./lib/DocumentsTab";
import Form from "nlib/common/Form";
import IntegrationServices from "const/IntegrationServices";
import Modal, { ModalFooter } from "nlib/ui/Modal";
import Objects from "utils/Objects";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import UiActions from "actions/UiActions";
import UncategorizedTab from "./lib/UncategorizedTab";
import UserRoles from "const/UserRoles";
import classNames from "classnames";
import moment from "moment";

const FIELDS = {
  advancedDocumentsWorkflow: false,
  recognizeDocumentLineItems: false,
  allowClientPayeeSelection: false,
  allowClientCategorySelection: false,
  allowClientClassSelection: false,
  allowClientLocationSelection: false,
  allowClientProjectSelection: false,
  allowClientTaxRateSelection: false,
  allowApproveWithoutPayee: false,
  allowAutoCreatePayee: false,
  defaultClientAccountantEmail: "",
  businessDescription: "",
  rpaClassEnabled: false,
  rpaTaxRateEnabled: true,
  rpaLocationEnabled: false,
  rpaProjectEnabled: false,
  rpaPayeeDocumentRule: false,
  rpaAmountDocumentRule: false,
  rpaAutoAskClient: false,
  rpaClientEmail: "",
  rpaAutoApprove: undefined,
  rpaStartFrom: "",
  rpaExcludedAccounts: [],
  rpaTransactionTypes: "",
  rpaExcludedWords: [],
  uncategorizedCategoryNames: [],
  rpaMatchingEnabled: true
};

const TABS = {
  AI_BOOKKEEPER_TAB_GENERAL: "aiBookkeeperTabGeneral",
  AI_BOOKKEEPER_TAB_PROCESSING: "aiBookkeeperTabProcessing",
  AI_BOOKKEEPER_TAB_AUTOMATION: "aiBookkeeperTabAutomation",
  AI_BOOKKEEPER_TAB_DOCUMENTS: "aiBookkeeperTabDocuments",
  AI_BOOKKEEPER_TAB_PROMPTS: "aiBookkeeperTabPrompts",
  CLIENT_PORTAL: "clientPortal",
  UNCATEGORIZED: "uncategorized",
  OCR_AI: "documents"
};

const AI_BOOKKEEPER_TABS = [
  TABS.AI_BOOKKEEPER_TAB_GENERAL,
  TABS.AI_BOOKKEEPER_TAB_PROCESSING,
  TABS.AI_BOOKKEEPER_TAB_AUTOMATION,
  TABS.AI_BOOKKEEPER_TAB_DOCUMENTS,
  TABS.AI_BOOKKEEPER_TAB_PROMPTS
];

const SYNC_SETTINGS = {
  RPA_START_FROM: "rpaStartFrom",
  RPA_TRANSACTION_TYPES: "rpaTransactionTypes",
  UNCATEGORIZED_CATEGORY_NAMES: "uncategorizedCategoryNames"
};

const checkRpaSettingsRequiredFields = (values, options) => {
  const {
    rpaMode,
    quickBooksBusiness,
    ignoreUncategorizedTransactions
  } = options;

  if (
    !rpaMode
    && quickBooksBusiness
    && !ignoreUncategorizedTransactions
    && !values.uncategorizedCategoryNames?.length
  ) {
    return false;
  }
  if (!rpaMode) return true;

  return Businesses.checkRpaSettingsRequiredFields({
    createdAt: options.createdAt,
    currentSavedRpaStartFrom: options.currentSavedRpaStartFrom,
    integrationService: options.integrationService,
    rpaUpsellDate: options.rpaUpsellDate,
    rpaStartFrom: values.rpaStartFrom,
    rpaTransactionTypes: values.rpaTransactionTypes,
    shouldRequireUncategorized: quickBooksBusiness
      && !ignoreUncategorizedTransactions
      && !values.processOnlyBankFeed,
    uncategorizedCategoryNames: values.uncategorizedCategoryNames,
    shouldRequireAskPerson: true,
    rpaAutoAskClient: values.rpaAutoAskClient,
    rpaClientEmail: values.rpaClientEmail,
    shouldRequireExcludedAccounts: values.rpaExcludedAccountsToggle,
    rpaExcludedAccounts: values.rpaExcludedAccounts,
    shouldRequireExcludedWords: values.rpaExcludedWordsToggle,
    rpaExcludedWords: values.rpaExcludedWords,
    shouldRequireMinTransactionAmount: values.rpaAmountDocumentRuleToggle,
    rpaAmountDocumentRule: values.rpaAmountDocumentRule
  });
};

const EditBusinessSettingsWindow = ({ businessId, defaultActiveTab, extraData = {}, onClose, ...restProps }) => {
  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const fetchingUsers = useSelector(checkUsersFetching);

  const { businessOrganization, ignoreUncategorizedTransactions } = useSelector(getActiveOrganization);

  const businessesData = useSelector(getAllBusinessesData);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const usersData = useSelector(getAllUsersData);

  const businessData = useMemo(() => {
    if (businessId === selectedBusinessData.id) {
      return selectedBusinessData || {};
    }

    return businessesData.find(({ id }) => id === businessId) || {};
  }, [businessId, selectedBusinessData, businessesData]);

  const hasBusinessUsers = useMemo(() => {
    return usersData.some(({ role, businessIds = [] }) => {
      return UserRoles.checkIsBusiness(role) && businessIds.includes(businessId);
    });
  }, [businessId, usersData]);

  const {
    createdAt,
    name: businessName,
    extraData: {
      integrationService,
      uncategorizedRpaEnabled,
      rpaUpsellDate
    } = {}
  } = businessData;

  const rpaMode = uncategorizedRpaEnabled !== undefined;

  const quickBooksBusiness = integrationService === IntegrationServices.QUICK_BOOKS.value;

  const [dataFetching, setDataFetching] = useState(false);

  const [upsellModalOpened, setUpsellModalOpened] = useState(false);

  const disabled = fetchingUsers || dataFetching;

  const [activeTab, setActiveTab] = useState(() => {
    if (rpaMode && defaultActiveTab === TABS.UNCATEGORIZED) {
      return TABS.AI_BOOKKEEPER_TAB_GENERAL;
    }

    return defaultActiveTab || (rpaMode ? TABS.AI_BOOKKEEPER_TAB_GENERAL : TABS.UNCATEGORIZED);
  });

  const syncSettingsKeys = useMemo(() => Object.values(SYNC_SETTINGS), []);

  const customPrompts = useSelector(getCustomPromptsData);

  const [customPromptsState, setCustomPromptsState] = useState(null);

  const businessUserEmails = useMemo(() => {
    return usersData
      .filter(({ role, businessIds = [] }) => UserRoles.checkIsBusiness(role) && businessIds.includes(businessId))
      .map(({ email }) => email.toLowerCase());
  }, [businessId, usersData]);

  const initialValues = useMemo(() => {
    const {
      rpaStartFrom,
      rpaClientEmail,
      rpaPaused,
      rpaExcludedAccounts,
      rpaExcludedWords = [],
      rpaAmountDocumentRule = false,
      uncategorizedCategoryNames = []
    } = businessData.settings || {};

    return {
      ...(Objects.getProps(businessData.settings || {}, Object.keys(FIELDS), FIELDS)),
      processOnlyBankFeed: !uncategorizedCategoryNames?.length,
      rpaAmountDocumentRule: rpaAmountDocumentRule === false ? "" : rpaAmountDocumentRule,
      rpaAmountDocumentRuleToggle: rpaAmountDocumentRule !== false,
      rpaExcludedAccountsToggle: !!rpaExcludedAccounts?.length,
      rpaExcludedWordsToggle: !!rpaExcludedWords?.length,
      rpaClientEmail: businessUserEmails.includes((rpaClientEmail || "").toLowerCase()) ? rpaClientEmail : "",
      rpaStartFrom: (rpaStartFrom && moment(rpaStartFrom).isValid()) ? Dates.formatApiDate(rpaStartFrom) : "",
      rpaEnabled: !rpaPaused
    };
  }, [businessUserEmails, businessData.settings]);

  const IconComponent = useMemo(() => {
    const serviceData = IntegrationServices.getByValue(integrationService);

    return serviceData && serviceData.logo ? () => <img className={Css.logo} src={serviceData.logo} /> : Icons.Briefcase;
  }, [integrationService]);

  const shouldNotifySyncSettings = useCallback((values) => {
    return syncSettingsKeys.some((key) => {
      switch (key) {
        case SYNC_SETTINGS.UNCATEGORIZED_CATEGORY_NAMES:
          if (!quickBooksBusiness || ignoreUncategorizedTransactions) return false;

          return !Objects.checkDeepEqualityByProps(values.uncategorizedCategoryNames, initialValues.uncategorizedCategoryNames);
        case SYNC_SETTINGS.RPA_TRANSACTION_TYPES: {
          if (!rpaMode) return false;

          const nextValue = values.rpaTransactionTypes || initialValues.rpaTransactionTypes;

          return nextValue !== initialValues.rpaTransactionTypes;
        }
        case SYNC_SETTINGS.RPA_START_FROM: {
          if (!rpaMode) return false;

          const rpaStartFromMinDate = Businesses.getRpaStartFromMinDate(
            integrationService,
            rpaUpsellDate,
            createdAt
          );

          const unchangedLegacyRpaStartFrom = values.rpaStartFrom
            && initialValues.rpaStartFrom
            && moment(values.rpaStartFrom).isValid()
            && moment(initialValues.rpaStartFrom).isValid()
            && moment(values.rpaStartFrom).isSame(initialValues.rpaStartFrom, "month");

          const rpaStartFromInvalid = moment(values.rpaStartFrom)
            .startOf("month")
            .isBefore(rpaStartFromMinDate) && !unchangedLegacyRpaStartFrom;

          const nextValue = values.rpaStartFrom && !rpaStartFromInvalid
            ? Dates.formatNoTimeZoneDate(values.rpaStartFrom)
            : initialValues.rpaStartFrom;

          const baseValue = initialValues.rpaStartFrom
            ? Dates.formatNoTimeZoneDate(initialValues.rpaStartFrom)
            : initialValues.rpaStartFrom;

          return nextValue !== baseValue;
        }
        default:
          return false;
      }
    });
  }, [
    initialValues,
    rpaMode,
    rpaUpsellDate,
    createdAt,
    integrationService,
    ignoreUncategorizedTransactions,
    syncSettingsKeys,
    quickBooksBusiness
  ]);

  const handleCloseClick = useCallback(() => {
    if (disabled) return;
    onClose();
  }, [disabled, onClose]);

  const handleFormSubmit = useCallback(async({ values, extraData: formExtraData }) => {
    const {
      settingsChanged = false,
      customPromptsChanged = false
    } = formExtraData || {};

    if (rpaMode && customPromptsChanged) {
      setDataFetching(true);

      const result = await dispatch(CustomPromptsActions.updateCustomPrompts(businessId, customPromptsState));

      if (result && !settingsChanged) {
        toast.success(messages.businessSettingsUpdated);
      }
    }
    if (settingsChanged) {
      const {
        rpaStartFrom,
        rpaClientEmail,
        rpaAutoAskClient,
        rpaTransactionTypes,
        rpaEnabled,
        rpaAutoApprove,
        rpaExcludedWords,
        rpaExcludedAccounts,
        rpaExcludedWordsToggle,
        rpaExcludedAccountsToggle,
        rpaClassEnabled,
        rpaTaxRateEnabled,
        rpaLocationEnabled,
        rpaProjectEnabled,
        rpaAmountDocumentRuleToggle,
        rpaAmountDocumentRule,
        uncategorizedCategoryNames,
        processOnlyBankFeed,
        ...restValues
      } = values;

      const rpaStartFromMinDate = Businesses.getRpaStartFromMinDate(
        integrationService,
        rpaUpsellDate,
        createdAt
      );

      const unchangedLegacyRpaStartFrom = rpaStartFrom
        && initialValues.rpaStartFrom
        && moment(rpaStartFrom).isValid()
        && moment(initialValues.rpaStartFrom).isValid()
        && moment(rpaStartFrom).isSame(initialValues.rpaStartFrom, "month");

      const rpaStartFromInvalid = moment(rpaStartFrom)
        .startOf("month")
        .isBefore(rpaStartFromMinDate) && !unchangedLegacyRpaStartFrom;

      const payload = {
        ...restValues,
        ...(quickBooksBusiness && !ignoreUncategorizedTransactions && {
          uncategorizedCategoryNames: rpaMode
            ? (processOnlyBankFeed ? [] : uncategorizedCategoryNames)
            : uncategorizedCategoryNames
        }),
        ...(rpaMode ? {
          rpaAmountDocumentRule: rpaAmountDocumentRuleToggle
            ? (
              (rpaAmountDocumentRule || rpaAmountDocumentRule === 0)
                ? rpaAmountDocumentRule : false
            ) : false,
          rpaAutoAskClient: !!(rpaAutoAskClient && rpaClientEmail),
          rpaExcludedWords: rpaExcludedWordsToggle
            ? (rpaExcludedWords?.length ? rpaExcludedWords : initialValues.rpaExcludedWords)
            : [],
          rpaClassEnabled: !!rpaClassEnabled,
          rpaTaxRateEnabled: !!rpaTaxRateEnabled,
          rpaLocationEnabled: !!rpaLocationEnabled,
          rpaProjectEnabled: !!rpaProjectEnabled,
          rpaStartFrom: rpaStartFrom && !rpaStartFromInvalid
            ? Dates.formatNoTimeZoneDate(rpaStartFrom) : initialValues.rpaStartFrom,
          rpaClientEmail,
          rpaAutoApprove: !!rpaAutoApprove,
          rpaExcludedAccounts: rpaExcludedAccountsToggle
            ? (rpaExcludedAccounts?.length ? rpaExcludedAccounts : initialValues.rpaExcludedAccounts)
            : [],
          rpaTransactionTypes: rpaTransactionTypes || initialValues.rpaTransactionTypes,
          rpaPaused: !rpaEnabled
        } : null)
      };

      if (settingsChanged && shouldNotifySyncSettings(payload)) {
        await dispatch(UiActions.showModal(
          messages.businessSettingsSyncWarning,
          uiTexts.notification,
          false,
          "sm"
        ));
      }
      setDataFetching(true);
      await dispatch(BusinessesActions.editBusinessSettings(businessId, payload));
    }
    setDataFetching(false);
    onClose();
  }, [
    initialValues,
    businessId,
    rpaMode,
    rpaUpsellDate,
    createdAt,
    integrationService,
    ignoreUncategorizedTransactions,
    customPromptsState,
    shouldNotifySyncSettings,
    quickBooksBusiness,
    messages,
    uiTexts,
    dispatch,
    setDataFetching,
    onClose
  ]);

  const handleFormChange = useCallback((name, value, prevValues) => {
    return { ...prevValues, [name]: value };
  }, []);

  const handleTabClick = useCallback((event) => {
    setActiveTab(event.currentTarget.dataset.tab);
  }, []);

  const handleCustomPromptsChange = useCallback((type = "", value) => {
    const [typePrefix, indexStr] = type.split(".");

    if (typePrefix === CustomPromptTypes.ADDITIONAL) {
      setCustomPromptsState((previousState) => {
        const additional = previousState.additional || [];

        return {
          ...previousState,
          additional: indexStr === undefined
            ? [...additional, { active: true, text: "" }]
            : (value === null
              ? additional.filter((item, itemIndex) => itemIndex !== +indexStr)
              : additional.map((item, itemIndex) => {
                return `${itemIndex}` === indexStr ? value : item;
              }))
        };
      });
    } else {
      setCustomPromptsState((previousState) => ({
        ...previousState,
        [type]: value
      }));
    }
  }, [setCustomPromptsState]);

  const handleUpsellModalOpen = useCallback(() => {
    setUpsellModalOpened(true);
  }, []);

  const handleUpsellModalClose = useCallback(() => {
    setUpsellModalOpened(false);
  }, []);

  useLayoutEffect(() => {
    if (!businessData.id) onClose();
  }, [businessData.id, onClose]);
  useEffect(() => {
    if (!rpaMode) return;
    dispatch(CustomPromptsActions.fetchCustomPrompts(businessId)).then((result) => {
      setCustomPromptsState(result);
    });
  }, [dispatch, businessId, rpaMode]);

  return (
    <Modal
      {...restProps}
      title={businessName}
      loading={dataFetching}
      className={classNames(Css.editBusinessSettingsWindow, upsellModalOpened && Css.hiddenModal)}
      dialogClassName={Css.dialogClassName}
      contentClassName={Css.contentClassName}
      iconComponent={IconComponent}
      onClose={handleCloseClick}>
      <Form
        disabled={fetchingUsers}
        initialValues={initialValues}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}>
        {({ onSubmit, values }) => {
          const submitDisabled = !checkRpaSettingsRequiredFields(values, {
            createdAt,
            currentSavedRpaStartFrom: initialValues.rpaStartFrom,
            integrationService,
            rpaMode,
            rpaUpsellDate,
            ignoreUncategorizedTransactions,
            quickBooksBusiness
          });

          return (
            <>
              <div className={Css.container} disabled={disabled}>
                <div className={Css.sidebar}>
                  {rpaMode && (
                    <>
                      <div className={classNames(Css.tab, AI_BOOKKEEPER_TABS.includes(activeTab) && Css.active)}>
                        {uiTexts.digitalBookkeeper}
                      </div>
                      <div className={Css.subTabs}>
                        <div
                          data-tab={TABS.AI_BOOKKEEPER_TAB_GENERAL}
                          className={classNames(Css.tab, activeTab === TABS.AI_BOOKKEEPER_TAB_GENERAL && Css.active)}
                          onClick={handleTabClick}>{uiTexts.general}</div>
                        <div
                          data-tab={TABS.AI_BOOKKEEPER_TAB_PROCESSING}
                          className={classNames(Css.tab, activeTab === TABS.AI_BOOKKEEPER_TAB_PROCESSING && Css.active)}
                          onClick={handleTabClick}>{uiTexts.processing}</div>
                        {values.advancedDocumentsWorkflow && (
                          <div
                            data-tab={TABS.AI_BOOKKEEPER_TAB_DOCUMENTS}
                            className={classNames(Css.tab, activeTab === TABS.AI_BOOKKEEPER_TAB_DOCUMENTS && Css.active)}
                            onClick={handleTabClick}>{uiTexts.documents}</div>
                        )}
                        <div
                          data-tab={TABS.AI_BOOKKEEPER_TAB_AUTOMATION}
                          className={classNames(Css.tab, activeTab === TABS.AI_BOOKKEEPER_TAB_AUTOMATION && Css.active)}
                          onClick={handleTabClick}>{uiTexts.automation}</div>
                        <div
                          data-tab={TABS.AI_BOOKKEEPER_TAB_PROMPTS}
                          className={classNames(Css.tab, activeTab === TABS.AI_BOOKKEEPER_TAB_PROMPTS && Css.active)}
                          onClick={handleTabClick}>{uiTexts.aiCustomization}</div>
                      </div>
                    </>
                  )}
                  {(!businessOrganization || hasBusinessUsers) && (
                    <div
                      data-tab={TABS.CLIENT_PORTAL}
                      className={classNames(Css.tab, activeTab === TABS.CLIENT_PORTAL && Css.active)}
                      onClick={handleTabClick}>
                      {uiTexts.clientPortal}
                    </div>
                  )}
                  {!rpaMode && (
                    <div
                      data-tab={TABS.UNCATEGORIZED}
                      className={classNames(Css.tab, activeTab === TABS.UNCATEGORIZED && Css.active)}
                      onClick={handleTabClick}>
                      {uiTexts.uncategorized}
                    </div>
                  )}
                  <div
                    data-tab={TABS.OCR_AI}
                    className={classNames(Css.tab, activeTab === TABS.OCR_AI && Css.active)}
                    onClick={handleTabClick}>
                    {uiTexts.documents}
                  </div>
                </div>
                <div className={Css.content}>
                  {(() => {
                    switch (activeTab) {
                      case TABS.CLIENT_PORTAL:
                        return <ClientPortalTab integrationService={integrationService} />;
                      case TABS.OCR_AI:
                        return <DocumentsTab integrationService={integrationService} rpaMode={rpaMode} />;
                      case TABS.AI_BOOKKEEPER_TAB_GENERAL:
                        return (
                          <AiBookkeeperTabGeneral
                            businessId={businessId}
                            businessData={businessData}
                            hasBusinessUsers={hasBusinessUsers}
                            onUpsellModalOpen={handleUpsellModalOpen}
                            onUpsellModalClose={handleUpsellModalClose} />
                        );
                      case TABS.AI_BOOKKEEPER_TAB_PROCESSING:
                        return (
                          <AiBookkeeperTabProcessing
                            businessId={businessId}
                            integrationService={integrationService}
                            ignoreUncategorizedTransactions={ignoreUncategorizedTransactions} />
                        );
                      case TABS.AI_BOOKKEEPER_TAB_AUTOMATION:
                        return <AiBookkeeperTabAutomation integrationService={integrationService} />;
                      case TABS.AI_BOOKKEEPER_TAB_DOCUMENTS:
                        return <AiBookkeeperTabDocuments />;
                      case TABS.AI_BOOKKEEPER_TAB_PROMPTS:
                        return customPromptsState ? (
                          <AiBookkeeperTabPrompts
                            customPrompts={customPromptsState}
                            extraData={extraData}
                            onChange={handleCustomPromptsChange} />
                        ) : <Preloader absolute />;
                      default:
                        return (
                          <UncategorizedTab
                            businessId={businessId}
                            integrationService={integrationService}
                            hasBusinessUsers={hasBusinessUsers} />
                        );
                    }
                  })()}
                </div>
              </div>
              <ModalFooter>
                <Buttons
                  disabled={disabled}
                  submitDisabled={submitDisabled}
                  initialValues={initialValues}
                  customPrompts={rpaMode ? (customPromptsState || customPrompts) : null}
                  initialCustomPrompts={rpaMode ? customPrompts : null}
                  rpaMode={rpaMode}
                  onSubmit={onSubmit}
                  onCancel={onClose} />
              </ModalFooter>
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export { TABS };

export default React.memo(EditBusinessSettingsWindow);
